import type { CreatePlayerRequest, PatchPlayerRequest, RoomPlayer } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { useState } from 'react';
import { changedFrom } from '../../dialogDrafts';
import { useUnsavedWork } from '../../unsaved';
import { btnGhost, btnPrimary, Field, FormSection, Modal, modalInput } from '../Modal';
import { keepTextOnEscape } from './searchFields';

/** First letter of each given part — the two parts are already separate, so no splitting. */
const initials = (first: string, last: string) =>
  [first, last]
    .map((part) => part.trim()[0]?.toUpperCase() ?? '')
    .join('');

/** Blank means "not recorded", which the contract spells null rather than "". */
const optional = (value: string) => (value.trim() === '' ? null : value.trim());

/**
 * Add a player, or edit one this room already holds.
 *
 * One dialog for both, over one set of fields — the arrangement the tournament create and edit
 * dialogs arrived at after holding two draft shapes that had started disagreeing about which
 * fields existed. What differs between the two modes here is the save mapping and two fields:
 * the card ID, which is not patchable, and the clear flags, which only an edit can need.
 */
export function PlayerModal({
  roomId,
  player,
  saving,
  errorMessage,
  onCreate,
  onPatch,
  onClose,
  initialCardId,
}: {
  roomId: string;
  /** The player being edited, or undefined to add one. */
  player?: RoomPlayer;
  /** A card to start a new player with — what was typed into a search before "not listed". */
  initialCardId?: string;
  saving: boolean;
  errorMessage: string | null;
  onCreate: (body: CreatePlayerRequest) => void;
  onPatch: (id: string, body: Partial<PatchPlayerRequest>) => void;
  onClose: () => void;
}) {
  const editing = player !== undefined;

  const [firstName, setFirstName] = useState(player?.player.firstName ?? '');
  const [lastName, setLastName] = useState(player?.player.lastName ?? '');
  const [cardId, setCardId] = useState(player?.player.cardId ?? initialCardId?.trim() ?? '');
  const [city, setCity] = useState(player?.player.city ?? '');
  const [state, setState] = useState(player?.player.state ?? '');
  const [avatar, setAvatar] = useState(player?.player.avatar ?? '');
  const [email, setEmail] = useState(player?.email ?? '');
  const [phone, setPhone] = useState(player?.phone ?? '');

  // #591: a switch of room, the section nav or closing the tab asks before dropping what was typed. Measured
  // from where the dialog opened — a card carried in from a search is not something the operator typed here.
  const fields = { firstName, lastName, cardId, city, state, avatar, email, phone };
  const [opened] = useState(fields);
  useUnsavedWork(changedFrom(opened, fields));

  const resolvedAvatar = avatar.trim() || initials(firstName, lastName) || '??';
  const canSave =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    (editing || cardId.trim().length > 0) &&
    !saving;

  const save = () => {
    if (!editing) {
      onCreate({
        roomId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        city: optional(city),
        state: optional(state),
        cardId: cardId.trim(),
        avatar: resolvedAvatar,
        email: optional(email),
        phone: optional(phone),
      });
      return;
    }

    // An emptied contact box becomes the explicit clear flag, and this translation is the whole
    // reason those flags exist. Null already means "leave alone", so a blank field cannot say
    // "remove this" — and the server refuses a blank value rather than guessing, because with no
    // operator link path a card with no contact is a card nobody can ever claim.
    //
    // Only when there was something to remove: sending clearEmail on a player who never had one
    // is a write that says a removal happened, and the audit stamp beside it would agree.
    const clearEmail = optional(email) === null && player.email !== null;
    const clearPhone = optional(phone) === null && player.phone !== null;

    onPatch(player.player.id, {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      avatar: resolvedAvatar,
      // City and state keep the ordinary empty-string convention — they are descriptive text, and
      // clearing one costs nobody anything.
      city: city.trim(),
      state: state.trim(),
      email: clearEmail ? null : optional(email),
      phone: clearPhone ? null : optional(phone),
      clearEmail,
      clearPhone,
    });
  };

  return (
    <Modal
      title={editing ? 'Edit player' : 'Add player'}
      subtitle={
        editing
          ? 'Your room’s record of this player. The card ID cannot be changed.'
          : 'Card IDs identify players when results are uploaded.'
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button type="button" onClick={save} disabled={!canSave} className={btnPrimary}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add player'}
          </button>
        </>
      }
    >
      {/* Every input here is `type="search"`, which is the one thing that kept Edge's "Saved info" from offering
          the desk's own name, address, email and phone into a customer's record — `autocomplete="off"` did not
          (#154). They keep the search field's native × to clear them, while `keepTextOnEscape` stops Esc clearing
          one, as it never did a text field. `spellCheck={false}` is on everything but the phone: names, a card
          and an address are not words, and Edge underlined them. */}
      <div className="space-y-5">
        <FormSection title="Player">
          {/* Two columns rather than a wrapping row: both are ordinary text inputs, so they
              shrink cleanly, unlike the date/time pair elsewhere. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="First name">
              <input
                type="search"
                autoComplete="off"
                spellCheck={false}
                onKeyDown={keepTextOnEscape}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g. Alex"
                className={modalInput}
              />
            </Field>
            <Field label="Last name">
              <input
                type="search"
                autoComplete="off"
                spellCheck={false}
                onKeyDown={keepTextOnEscape}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="e.g. Moreno"
                className={modalInput}
              />
            </Field>
          </div>

          <Field
            label="Card ID"
            hint={
              editing
                ? 'Fixed. A card number is how your room names a person, so a different card is a different player.'
                : undefined
            }
          >
            <input
              type="search"
              autoComplete="off"
              spellCheck={false}
              onKeyDown={keepTextOnEscape}
              value={cardId}
              onChange={(e) => setCardId(e.target.value)}
              placeholder="e.g. SHR-00142"
              className={modalInput}
              disabled={editing}
            />
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="City (optional)">
              <input
                type="search"
                autoComplete="off"
                spellCheck={false}
                onKeyDown={keepTextOnEscape}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Hollywood"
                className={modalInput}
              />
            </Field>
            <Field label="State (optional)">
              <input
                type="search"
                autoComplete="off"
                spellCheck={false}
                onKeyDown={keepTextOnEscape}
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="e.g. FL"
                className={modalInput}
              />
            </Field>
          </div>

          <Field
            label="Avatar initials (optional)"
            hint={`Defaults to initials from the name — currently “${resolvedAvatar}”.`}
          >
            <input
              type="search"
              autoComplete="off"
              spellCheck={false}
              onKeyDown={keepTextOnEscape}
              value={avatar}
              onChange={(e) => setAvatar(e.target.value.slice(0, 3))}
              placeholder={initials(firstName, lastName) || 'AM'}
              className={modalInput}
            />
          </Field>
        </FormSection>

        {/* Its own section, and the hint says what it is for rather than what it is. A room
            filling these in is not recording an address for its own sake — it is the only thing
            that lets this player ever connect their own account to this card. */}
        <FormSection
          title="Contact"
          hint={editing && player.contactUpdatedAt ? 'Changes are recorded' : undefined}
        >
          <Field
            label="Email (optional)"
            hint="How this player proves the card is theirs when they connect an account."
          >
            <input
              type="search"
              autoComplete="off"
              spellCheck={false}
              onKeyDown={keepTextOnEscape}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. alex.moreno@example.com"
              className={modalInput}
              inputMode="email"
            />
          </Field>
          <Field
            label="Phone (optional)"
            hint="Recorded now; verification codes are sent by email today."
          >
            <input
              type="search"
              autoComplete="off"
              onKeyDown={keepTextOnEscape}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. (954) 555-0142"
              className={modalInput}
              inputMode="tel"
            />
          </Field>
        </FormSection>

        <ErrorNotice message={errorMessage} />
      </div>
    </Modal>
  );
}
